import { useTraduccion } from '@tallerya/i18n';
import { Ayuda, AvisoError, Boton, IconoMas, Menu, type ElementoMenu } from '@tallerya/ui';
import { Link } from 'react-router';
import { usePermisos } from '../../permisos/usePermiso';
import { useAccionesGasto } from '../gastos/AccionesGasto';
import { useGastosDeOrden, useMontosGastos, type Gasto, type MontoGasto } from '../gastos/datos';
import { useFormatoGasto } from '../gastos/formato';
import type { CabeceraOrden } from './datos';

// «Gastos de esta orden» en la ficha (#143), como en `190:9042`: lo que se compró
// para este trabajo, con su proveedor, su factura y si ya se pagó.
//
//   Gastos de esta orden ⓘ                          [+ Registrar gasto]
//   🧾 Pastillas y líquido de frenos       [Pendiente de pago]   $52.00  •••
//      Súper Repuestos · Fact. 00482 · hoy · registró Carlos Méndez
//
// - **La sección pide `expense.create`**: sin él la API no lista gastos, y no se
//   dibuja. Registrar pide lo mismo.
// - **El monto, solo con `expense.amount.view`**, leído de su propio endpoint,
//   los de la sección en un pedido. Cambiarlo pide `expense.amount.edit`.
// - El «•••» y sus ventanas son los del listado de la caja (#144):
//   `useAccionesGasto`. El nombre del gasto abre su ficha (#189), con el historial.
//
// Lo que no sigue al Figma, a propósito:
//
//   - «Compras hechas para este trabajo» va tras el ⓘ del título y no fija
//     debajo (#120): se lee una vez y después estorba.

export function SeccionGastos({ orden }: { orden: CabeceraOrden }) {
  const { t } = useTraduccion();
  const { puede } = usePermisos();
  const id = String(orden.id);
  const registra = puede('expense.create');
  const veMontos = puede('expense.amount.view');

  const consulta = useGastosDeOrden(id, registra);
  const gastos = consulta.data ?? [];
  const montos = useMontosGastos(
    gastos.map((g) => String(g.id)),
    veMontos,
  );
  const { acciones, registrar, problema, enCurso, ventanas } = useAccionesGasto({
    locationId: String(orden.locationId),
    serviceOrderId: id,
  });

  if (!registra) return null;

  /** El monto leído de un gasto, si se leyó bien: lo que no se sabe no se dibuja. */
  const montoDeGasto = (gasto: Gasto): MontoGasto | undefined =>
    montos.isError ? undefined : montos.data?.get(String(gasto.id));

  return (
    <section className="orden__seccion">
      <header className="orden__seccion-cabecera">
        <div className="titulo-con-ayuda titulo-con-ayuda--seccion">
          <h2>{t('admin.gastos.titulo')}</h2>
          <Ayuda tema={t('admin.gastos.titulo')} texto={t('admin.gastos.info')} tono="titulo" />
        </div>
        <Boton variante="secundario" icono={<IconoMas />} onClick={registrar}>
          {t('admin.gastos.registrar')}
        </Boton>
      </header>

      {problema && <AvisoError aviso={{ titulo: t('admin.gastos.noSePudo'), texto: problema }} />}

      {consulta.isPending ? (
        <p className="ficha__nota">{t('app.cargando')}</p>
      ) : consulta.isError ? (
        <div className="ficha__vacio">
          <p className="ficha__vacio-titulo">{t('ui.listado.error.titulo')}</p>
          <Boton variante="secundario" onClick={() => void consulta.refetch()}>
            {t('app.reintentar')}
          </Boton>
        </div>
      ) : gastos.length === 0 ? (
        <div className="ficha__vacio">
          <p className="ficha__vacio-titulo">{t('admin.gastos.vacio')}</p>
          <p>{t('admin.gastos.vacioTexto')}</p>
        </div>
      ) : (
        <ul className="orden__lineas">
          {gastos.map((gasto) => (
            <FilaGasto
              key={String(gasto.id)}
              gasto={gasto}
              // `undefined`: sin permiso o sin leer, no se dibuja.
              monto={veMontos ? montoDeGasto(gasto) : undefined}
              esperando={enCurso === String(gasto.id)}
              acciones={acciones(gasto, montoDeGasto(gasto))}
            />
          ))}
        </ul>
      )}

      {ventanas}
    </section>
  );
}

function FilaGasto({
  gasto,
  monto,
  esperando,
  acciones,
}: {
  gasto: Gasto;
  monto: MontoGasto | undefined;
  esperando: boolean;
  acciones: ElementoMenu[];
}) {
  const { t } = useTraduccion();
  const { dia, cifra } = useFormatoGasto();

  const detalle = [
    gasto.supplierName,
    gasto.documentNumber && t('admin.gastos.factura', { numero: gasto.documentNumber }),
    dia(gasto.purchasedOn),
    gasto.createdByName && t('admin.gastos.registro', { persona: gasto.createdByName }),
  ]
    .filter(Boolean)
    .join(' · ');

  const texto = monto ? cifra(monto) : undefined;

  return (
    <li className="orden__linea gasto__fila" aria-busy={esperando}>
      <span className="gasto__icono" aria-hidden="true">
        🧾
      </span>
      <div className="orden__linea-cuerpo">
        <p className="orden__linea-nombre">
          <Link className="tipos__enlace" to={`/gastos/${String(gasto.id)}`} state={{ desde: 'orden' }}>
            {gasto.description}
          </Link>
        </p>
        <p className="orden__linea-detalle">{detalle}</p>
      </div>
      <span className={`orden__marca orden__marca--${gasto.paidOn ? 'neutro' : 'aviso'}`}>
        {gasto.paidOn ? t('admin.gastos.pagado', { fecha: dia(gasto.paidOn) }) : t('admin.gastos.pendiente')}
      </span>
      {texto !== undefined && (
        <span className={`orden__linea-monto${texto === null ? ' orden__linea-monto--sin' : ''}`}>
          {texto ?? t('admin.gastos.sinMonto')}
        </span>
      )}
      {acciones.length > 0 && (
        <Menu
          elementos={acciones}
          disparador={({ alternar, atributos }) => (
            <Boton
              variante="texto"
              soloIcono
              disabled={esperando}
              aria-label={t('admin.gastos.acciones', { gasto: gasto.description })}
              onClick={alternar}
              {...atributos}
            >
              •••
            </Boton>
          )}
        />
      )}
    </li>
  );
}
